import { Button, Dropdown, Layout, Menu, Drawer } from 'antd';
import { useState, useEffect } from 'react';
import './layout.css'
import {
    MenuOutlined,
    SearchOutlined,
    CalculatorOutlined,
    CreditCardOutlined,
    FileTextOutlined,
    MessageOutlined,
    MedicineBoxOutlined,
    BellOutlined,
    BgColorsOutlined,
    BugOutlined,
} from "@ant-design/icons";
import PriceQuoteModal from "../../../../app/price-quote/PriceQuoteModal";
import { useSearch } from "../../../context/SearchContext";
import Flex from "../../Flex";
import Logo from "./Logo";
import ProfileDropdown from "./ProfileDropdown";
import Footer from "./Footer";
import BottomNav, { BOTTOM_NAV_HEIGHT } from "./BottomNav";
import { useTranslation } from "react-i18next";
import { Route, useHistory, useLocation } from "react-router-dom";
import { PERMISSIONS, useUser } from "../../../context/UserContext";
import { BrowserView, isMobile, MobileView } from "react-device-detect";
import { CalendarOutlined, TableOutlined } from "@ant-design/icons";
import { GiMedicalPack } from "react-icons/gi";
import { FaClinicMedical, FaCashRegister } from "react-icons/fa";
import { AiOutlinePlus, AiOutlineApartment, AiOutlineAreaChart, AiFillDatabase } from "react-icons/ai";
import { BsFillCalendarDateFill, BsCashStack, BsFillPersonBadgeFill } from 'react-icons/bs';
import { HiUserGroup } from 'react-icons/hi';
import NotificationMenu from "../../../../app/NotificationMenu";
import { Branding } from "../../../service/BrandingService";

const { Header } = Layout;

const { SubMenu } = Menu;

const Icon = ({ icon }) => {
    return (
        <div className={'anticon antitem'}>
            {icon}
        </div>
    )
}

const DefinitionsIcon = () => {
    return <Icon icon={<AiFillDatabase />} />
}

const DashboardIcon = () => {
    return <Icon icon={<AiOutlineAreaChart />} />
}

const CashRegisterIcon = () => {
    return <Icon icon={<FaCashRegister />} />
}

const DoctorIcon = () => {
    return <Icon icon={<GiMedicalPack />} />
}

const UnitIcon = () => {
    return <Icon icon={<AiOutlineApartment />} />
}

const OperationIcon = () => {
    return <Icon icon={<AiOutlinePlus />} />
}

const InstitutionIcon = () => {
    return <Icon icon={<FaClinicMedical />} />
}

const NewAppointmentIcon = () => {
    return <Icon icon={<BsFillCalendarDateFill />} />
}

const Expense = () => {
    return <Icon icon={<BsCashStack />} />
}

const Patient = () => {
    return <Icon icon={<BsFillPersonBadgeFill />} />
}

const UserIcon = () => {
    return <Icon icon={<HiUserGroup />} />
}

export default function MainLayout({ children }) {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const key = window.location.pathname;
    const { hasPermission } = useUser()
    const [menuVisible, setMenuVisible] = useState(false);
    const { openSearch } = useSearch();
    const [quoteOpen, setQuoteOpen] = useState(false);
    // Mac kullanıcıları için ⌘, diğerleri için Ctrl
    const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/.test(navigator.platform);
    const shortcutLabel = isMac ? '⌘E' : 'Ctrl+E';
    const quoteShortcutLabel = isMac ? '⌘D' : 'Ctrl+D';
    // Fiyat Teklifi baştan sona fiyat gösterdiği için ayrı bir izni yok; fiyat görme izniyle açılır.
    const canUsePriceQuote = hasPermission(PERMISSIONS.VIEW_PRICE);

    // Kasa panosu dar ekranda her pikseli kullanıyor (grafik, kutucuklar); alt bar orada gizlenir.
    // /app/dashboard yetkisi olmayanda Metabase panosunu açıyor — bar yalnız kasa panosunda kalkar.
    const hideBottomNav = location.pathname.replace(/\/+$/, '') === '/app/dashboard'
        && hasPermission(PERMISSIONS.CASH_DASHBOARD);

    // Cmd/Ctrl+D → Fiyat Teklifi modal'ı aç (browser bookmark shortcut override edilir).
    // İzin yoksa dinleyici hiç kurulmaz, kısayol tarayıcıya geri bırakılır.
    useEffect(() => {
        if (!canUsePriceQuote) return;

        const onKey = (e) => {
            const isMeta = e.metaKey || e.ctrlKey;
            if (isMeta && !e.shiftKey && !e.altKey && (e.key === 'd' || e.key === 'D')) {
                e.preventDefault();
                setQuoteOpen(true);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [canUsePriceQuote]);

    // Header rengi MARKADAN gelir (DB). Kiracı belirlemediyse marka slate'i kullanılır.
    // Metin rengi luma'ya göre otomatik hesaplanır → açık renkli header'da da okunur kalır.
    const headerColor = Branding.headerColor();

    // Simple helper to check if color is dark to set text color
    const isDark = (color) => {
        if (!color || color.startsWith('rgba') || color === 'transparent') return false;
        const c = color.substring(1);      // strip #
        const rgb = parseInt(c, 16);   // convert rrggbb to decimal
        const r = (rgb >> 16) & 0xff;  // extract red
        const g = (rgb >> 8) & 0xff;  // extract green
        const b = (rgb >> 0) & 0xff;  // extract blue
        const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
        return luma < 128;
    }

    const textColor = isDark(headerColor) ? '#ffffff' : '#595959';
    const menuTheme = isDark(headerColor) ? 'dark' : 'light';

    // Çekmeceler (mobil menü, profil) portal ile <body> altına render edilir; #site-header'ın
    // üzerindeki inline değişkenleri göremezler. Header rengini köke yayınlıyoruz ki çekmece
    // başlığı da kiracının header rengiyle aynı olsun — logo o zemin için tasarlandı.
    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--header-bg', headerColor);
        root.style.setProperty('--header-fg', textColor);
    }, [headerColor, textColor]);

    const getMenuItem = (key, text) => {
        return (
            <Menu.Item key={key} icon={<CalendarOutlined />}>
                <a href={key}>{text}</a>
            </Menu.Item>
        )
    }

    const hasSystemPermission = () => {
        return hasPermission(PERMISSIONS.FIRM_UNIT_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_DOCTOR_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_CASH_REGISTER) ||
            hasPermission(PERMISSIONS.FIRM_USER_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_USER_MANAGER) ||
            hasPermission(PERMISSIONS.APPLOG_VIEW) ||
            hasPermission(PERMISSIONS.ENABIZ_OPERATION_TRACKING) ||
            hasPermission(PERMISSIONS.NOTIFICATION_SETTINGS_MANAGER) ||
            hasPermission(PERMISSIONS.BRANDING_MANAGE)
    }

    const getMenu = () => {
        return (
            <>
                {/* Tek giriş: kasa panosu yetkisi varsa /app/dashboard onu, yoksa Metabase panosunu açar.
                    (Metabase panosu ayrıca /app/dashboard/analytics adresinde.) */}
                {(hasPermission(PERMISSIONS.CASH_DASHBOARD) || hasPermission(PERMISSIONS.DASHBOARD)) &&
                    <Menu.Item key={`/app/dashboard`} icon={<DashboardIcon />}>{t('Dashboard')}</Menu.Item>}

                {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && <Menu.Item key={`/app/appointments/calendar`} icon={<CalendarOutlined />}>{t('Calendar')}</Menu.Item>}
                {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && <Menu.Item key={`/app/appointments/grid`} icon={<TableOutlined />}>{t('Appointments')}</Menu.Item>}
                {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && <Menu.Item key={`/app/patients/grid`} icon={<Patient />}>{t('Patients')}</Menu.Item>}
                {hasPermission(PERMISSIONS.EXPENSE_VIEW) && <Menu.Item key={`/app/expenses`} icon={<Expense />}>{t('Expenses')}</Menu.Item>}
                {hasPermission(PERMISSIONS.CASH_REGISTER_VIEW) && <Menu.Item key={`/app/cash-view`} icon={<CashRegisterIcon />}>{t('Cash Register')}</Menu.Item>}
                <Menu.Item key={`/app/system/operations`} icon={<OperationIcon />}>{t('Operations')}</Menu.Item>
                <Menu.Item key={`/app/system/institutions`} icon={<InstitutionIcon />}>{t('Institutions')}</Menu.Item>

                {
                    hasSystemPermission() && (
                        <SubMenu key={'system'} title={t('System')} icon={<DefinitionsIcon />}>
                            {hasPermission(PERMISSIONS.FIRM_UNIT_MANAGER) && <Menu.Item key={`/app/system/units`} icon={<UnitIcon />}>{t('Units')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.FIRM_DOCTOR_MANAGER) && <Menu.Item key={`/app/system/doctors`} icon={<DoctorIcon />}>{t('Doctors')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.FIRM_CASH_REGISTER) && <Menu.Item key={`/app/system/cash-registers`} icon={<CashRegisterIcon />}>{t('Cash Registers')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.FIRM_CASH_REGISTER) && <Menu.Item key={`/app/system/pos-devices`} icon={<Icon icon={<CreditCardOutlined />} />}>{t('POS Devices')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.FIRM_USER_MANAGER) && <Menu.Item key={`/app/system/roles`} icon={<UserIcon />}>{t('Roles')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.FIRM_USER_MANAGER) && <Menu.Item key={`/app/system/users`} icon={<UserIcon />}>{t('Users')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.APPLOG_VIEW) && <Menu.Item key={`/app/applog/grid`} icon={<Icon icon={<FileTextOutlined />} />}>{t('Logs')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.APPLOG_VIEW) && <Menu.Item key={`/app/system/system-logs`} icon={<Icon icon={<BugOutlined />} />}>{t('System Logs')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.APPLOG_VIEW) && <Menu.Item key={`/app/sms-logs/grid`} icon={<Icon icon={<MessageOutlined />} />}>{t('SMS Logs')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.ENABIZ_OPERATION_TRACKING) && <Menu.Item key={`/app/system/enabiz-operations`} icon={<Icon icon={<MedicineBoxOutlined />} />}>{t('Enabiz Operation Tracking')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.NOTIFICATION_SETTINGS_MANAGER) && <Menu.Item key={`/app/system/notification-settings`} icon={<Icon icon={<BellOutlined />} />}>{t('Notification Settings')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.BRANDING_MANAGE) && <Menu.Item key={`/app/system/branding`} icon={<Icon icon={<BgColorsOutlined />} />}>{t('Branding')}</Menu.Item>}
                            {<Menu.Item key={`/app/patient-queue/grid`} icon={<Patient />}>{t('Patient Queue')}</Menu.Item>}
                        </SubMenu>
                    )
                }
            </>
        )
    }

    const getMobileMenu = () => {
        return (
            <Menu
                style={{ marginLeft: 16, minWidth: 128 }}
                onClick={(item) => {
                    history.push(item.key)
                }}
                theme="dark"
                defaultSelectedKeys={[key]}
            >
                {getMenu()}
            </Menu>
        )
    }

    return (
        <Layout className={`app-shell${hideBottomNav ? ' app-shell--no-bottom-nav' : ''}`}>
            <MobileView>
                <Header id={'site-header'} style={{
                    position: 'fixed',
                    zIndex: 100,
                    width: '100%',
                    height: 64,
                    padding: '0 16px',
                    backgroundColor: headerColor,
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    color: textColor,
                    // header rengi kiracıya göre değişiyor → stiller CSS değişkenine bağlanır (layout.css)
                    '--header-fg': textColor,
                    '--header-bg': headerColor,
                }}>
                    <Flex alignItems={'center'} flexWrap={'nowrap'} justifyContent={'space-between'} style={{ width: '100%', height: '100%' }}>
                        <Button
                            type="text"
                            shape="circle"
                            icon={<MenuOutlined style={{ color: textColor, fontSize: '20px' }} />}
                            onClick={() => setMenuVisible(true)}
                            style={{ height: 40, width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        />
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                            <Logo />
                        </div>
                        <ProfileDropdown isMobile={true} />
                    </Flex>
                </Header>
                <Drawer
                    title={<Logo />}
                    placement="left"
                    closable={true}
                    onClose={() => setMenuVisible(false)}
                    open={menuVisible}
                    /* bodyStyle v5'te kaldırıldı (deprecation uyarısı basıyordu) */
                    styles={{ body: { padding: 0 } }}
                    /* CSS ile hesaplanır: ekran döndüğünde/yeniden boyutlandığında da doğru
                       (Math.min(window.innerWidth) ilk render'da donuyordu) */
                    width="min(86vw, 320px)"
                    className="mobile-nav-drawer nav-drawer"
                >
                    <Menu
                        mode="inline"
                        theme="light"
                        onClick={(item) => {
                            history.push(item.key)
                            setMenuVisible(false)
                        }}
                        /* defaultSelectedKeys yalnız ilk mount'ta okunur; çekmece açık kalıp
                           rota değişince seçim eski satırda takılı kalıyordu. */
                        selectedKeys={[key]}
                        style={{ border: 'none' }}
                    >
                        {getMenu()}
                    </Menu>
                </Drawer>
                {!hideBottomNav && <BottomNav onOpenQuote={canUsePriceQuote ? () => setQuoteOpen(true) : undefined} />}
            </MobileView>
            <BrowserView>
                <Header id={'site-header'} style={{
                    position: 'fixed',
                    zIndex: 100,
                    width: '100%',
                    backgroundColor: headerColor,
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    color: textColor,
                    '--header-fg': textColor,
                    '--header-bg': headerColor,
                }}>
                    <Flex alignItems={'center'}>
                        <Logo />
                        <Menu
                            onClick={(item) => {
                                history.push(item.key)
                            }}
                            theme={menuTheme}
                            mode="horizontal"
                            /* açılır menü <body> altına portal ile gider; header'la aynı
                               zemini kullanabilmek için sınıf veriyoruz (layout.css) */
                            popupClassName="header-menu-popup"
                            defaultSelectedKeys={[key]}
                            style={{ marginLeft: 16, flex: 1, background: 'transparent', borderBottom: 'none', color: textColor }}
                        >
                            {getMenu()}
                        </Menu>
                        {/* Spotlight-tarzı arama tetikleyici — her sayfada erişilebilir,
                            Cmd/Ctrl+E kısayolu ile de açılır */}
                        <button
                            type="button"
                            className="header-search"
                            onClick={openSearch}
                            title={`${t('Search')} (${shortcutLabel})`}
                        >
                            <span className="header-search-label">
                                <SearchOutlined />
                                <span>{t('Search...')}</span>
                            </span>
                            <kbd className="header-kbd">{shortcutLabel}</kbd>
                        </button>
                        {/* Fiyat Teklifi tetikleyici — Cmd/Ctrl+D kısayolu */}
                        {canUsePriceQuote && (
                        <button
                            type="button"
                            className="header-search header-quote"
                            onClick={() => setQuoteOpen(true)}
                            title={`${t('Price Quote')} (${quoteShortcutLabel})`}
                        >
                            <span className="header-search-label">
                                <CalculatorOutlined />
                                <span>{t('Quote')}</span>
                            </span>
                            <kbd className="header-kbd">{quoteShortcutLabel}</kbd>
                        </button>
                        )}
                        <NotificationMenu />
                        <ProfileDropdown />
                    </Flex>
                </Header>
            </BrowserView>
            <Layout
                className="site-layout"
                style={{
                    // Mobilde bottom nav var; içeriğin üzerine binmemesi için altta yer bırak
                    paddingBottom: isMobile && !hideBottomNav ? BOTTOM_NAV_HEIGHT + 8 : undefined,
                }}
            >
                {children}
            </Layout>
            {/* Footer site-layout'un DIŞINDA: sayfalar Sider kullanıyor, antd o Layout'a
                .ant-layout-has-sider verip ekseni YATAY yapıyor ve içerideki footer sağa savruluyor. */}
            <Footer />

            {/* Global Fiyat Teklifi Modal — header butonu veya Cmd/Ctrl+D ile açılır */}
            {canUsePriceQuote && (
                <PriceQuoteModal
                    open={quoteOpen}
                    onClose={() => setQuoteOpen(false)}
                />
            )}
        </Layout>
    )
}
